import { useState, useEffect, useCallback } from "react";
import { Loader2, Sparkles, Wand2, History, ListTree, BookOpen, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import KiBuchungssatzResult from "./KiBuchungssatzResult";
import Skr51AccountPicker from "./Skr51AccountPicker";
import SKR51KontenplanTab from "./SKR51KontenplanTab";
import BuchungsstapelPanel from "./BuchungsstapelPanel";

const eur = (n) => `${(Number(n) || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

/**
 * Jun-2026 (User-Wunsch) — KI-gestütztes SKR03/04-Buchungssatz-Vorschlagstool.
 * Gibt zu einem Geschäftsvorfall den passenden Buchungssatz (Soll/Haben +
 * Steuerschlüssel) im gewünschten Kontenrahmen vor (Universal-LLM-Key).
 */
export default function KiBuchungssatzTab() {
    const [form, setForm] = useState({
        buchungstext: "", betrag_brutto: "", ust_satz: 19,
        richtung: "ausgabe", kontenrahmen: "SKR03", geschaeftspartner: "", zahlungsart: "bank",
    });
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [history, setHistory] = useState([]);
    // Jun-2026 (User-Wunsch b) — manuelle SKR51-Soll/Haben-Auswahl
    const [sollKonto, setSollKonto] = useState(null);
    const [habenKonto, setHabenKonto] = useState(null);
    // Jun-2026 (User-Wunsch) — Kontenplan aufklappbar (nur bei SKR51) + Buchungsstapel
    const [showKontenplan, setShowKontenplan] = useState(false);
    const [stapelSignal, setStapelSignal] = useState(0);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const addToStapel = async (res) => {
        if (!res?.betrag_brutto || Number(res.betrag_brutto) <= 0) {
            toast.error("Bitte zuerst einen Bruttobetrag (> 0) eingeben");
            return;
        }
        try {
            await api.post("/buchhaltung/buchungsstapel", {
                soll_konto: res.soll_konto,
                soll_konto_name: res.soll_konto_name,
                haben_konto: res.haben_konto,
                haben_konto_name: res.haben_konto_name,
                steuerschluessel: res.steuerschluessel || null,
                kontenrahmen: res.kontenrahmen,
                buchungstext: res.buchungstext || "",
                betrag_brutto: Number(res.betrag_brutto) || 0,
                betrag_netto: res.betrag_netto ?? null,
                ust_satz: res.ust_satz ?? null,
                ust_betrag: res.ust_betrag ?? null,
                richtung: res.richtung || null,
            });
            setStapelSignal((s) => s + 1);
            toast.success("Zum DATEV-Buchungsstapel hinzugefügt");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Konnte nicht hinzugefügt werden");
        }
    };

    const buildManual = () => {
        if (!sollKonto?.konto || !habenKonto?.konto) {
            toast.error("Bitte Soll- und Haben-Konto wählen");
            return;
        }
        const brutto = Number(form.betrag_brutto) || 0;
        if (brutto <= 0) {
            toast.error("Bitte einen Bruttobetrag (> 0) eingeben");
            return;
        }
        const ust = Number(form.ust_satz) || 0;
        const netto = ust ? Math.round((brutto / (1 + ust / 100)) * 100) / 100 : brutto;
        setResult({
            kontenrahmen: "SKR51",
            richtung: form.richtung,
            buchungstext: form.buchungstext || "Manueller Buchungssatz (SKR51)",
            soll_konto: sollKonto.konto,
            soll_konto_name: sollKonto.bezeichnung,
            haben_konto: habenKonto.konto,
            haben_konto_name: habenKonto.bezeichnung,
            steuerschluessel: "",
            steuer_hinweis: "",
            begruendung: "Manuell aus dem SKR51-Kontenplan gewählt.",
            konfidenz: null,
            alternativen: [],
            betrag_brutto: brutto,
            betrag_netto: netto,
            ust_satz: ust,
            ust_betrag: Math.round((brutto - netto) * 100) / 100,
            model: "manuell · SKR51",
        });
        toast.success("Buchungssatz aus SKR51 übernommen");
    };

    const loadHistory = useCallback(async () => {
        try { const r = await api.get("/buchhaltung/ki-buchungssatz/history"); setHistory(r.data?.items || []); }
        catch { /* ignore */ }
    }, []);
    useEffect(() => { loadHistory(); }, [loadHistory]);

    const submit = async () => {
        if (!form.buchungstext.trim()) { toast.error("Bitte einen Buchungstext eingeben"); return; }
        if (!form.betrag_brutto || Number(form.betrag_brutto) <= 0) { toast.error("Bitte einen Bruttobetrag eingeben"); return; }
        setLoading(true);
        setResult(null);
        try {
            const r = await api.post("/buchhaltung/ki-buchungssatz", {
                buchungstext: form.buchungstext.trim(),
                betrag_brutto: Number(form.betrag_brutto),
                ust_satz: Number(form.ust_satz),
                richtung: form.richtung,
                kontenrahmen: form.kontenrahmen,
                geschaeftspartner: form.geschaeftspartner.trim() || null,
                zahlungsart: form.zahlungsart || null,
            });
            setResult(r.data);
            loadHistory();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "KI-Vorschlag fehlgeschlagen");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="ki-buchungssatz-tab">
            {/* Eingabe */}
            <div className="lg:col-span-2 bg-white border border-gray-200 rounded-sm p-5">
                <div className="flex items-center gap-2 mb-1">
                    <Wand2 className="w-5 h-5 text-[#111827]" />
                    <h2 className="font-display font-black text-lg">KI-Buchungssatz</h2>
                </div>
                <p className="text-xs text-gray-500 mb-4">Geschäftsvorfall beschreiben — die KI schlägt den passenden Buchungssatz (SKR03/04/51) vor.</p>

                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Buchungstext / Beleg</label>
                <textarea value={form.buchungstext} onChange={(e) => set("buchungstext", e.target.value)} rows={2}
                    placeholder="z. B. Rechnung Werkstattbedarf Louis, Bremsbeläge"
                    className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm mt-1 mb-3" data-testid="ki-bk-text" />

                <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Betrag (brutto €)</label>
                        <Input type="number" step="0.01" value={form.betrag_brutto} onChange={(e) => set("betrag_brutto", e.target.value)} className="rounded-sm mt-1 font-mono-num" data-testid="ki-bk-betrag" />
                    </div>
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">USt-Satz</label>
                        <select value={form.ust_satz} onChange={(e) => set("ust_satz", e.target.value)} className="w-full h-10 rounded-sm border border-gray-300 px-2 text-sm mt-1" data-testid="ki-bk-ust">
                            <option value={19}>19 %</option>
                            <option value={7}>7 %</option>
                            <option value={0}>0 % / steuerfrei</option>
                        </select>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Richtung</label>
                        <select value={form.richtung} onChange={(e) => set("richtung", e.target.value)} className="w-full h-10 rounded-sm border border-gray-300 px-2 text-sm mt-1" data-testid="ki-bk-richtung">
                            <option value="ausgabe">Ausgabe / Aufwand</option>
                            <option value="einnahme">Einnahme / Erlös</option>
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Kontenrahmen</label>
                        <select value={form.kontenrahmen} onChange={(e) => set("kontenrahmen", e.target.value)} className="w-full h-10 rounded-sm border border-gray-300 px-2 text-sm mt-1" data-testid="ki-bk-kr">
                            <option value="SKR03">SKR03</option>
                            <option value="SKR04">SKR04</option>
                            <option value="SKR51">SKR51 (Kfz)</option>
                        </select>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Geschäftspartner (optional)</label>
                        <Input value={form.geschaeftspartner} onChange={(e) => set("geschaeftspartner", e.target.value)} placeholder="z. B. Louis GmbH" className="rounded-sm mt-1" data-testid="ki-bk-partner" />
                    </div>
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Zahlungsart</label>
                        <select value={form.zahlungsart} onChange={(e) => set("zahlungsart", e.target.value)} className="w-full h-10 rounded-sm border border-gray-300 px-2 text-sm mt-1" data-testid="ki-bk-zahlungsart">
                            <option value="bank">Bank</option>
                            <option value="kasse">Kasse</option>
                            <option value="offen">Offen (Ziel)</option>
                        </select>
                    </div>
                </div>

                <Button onClick={submit} disabled={loading} className="w-full rounded-sm bg-[#111827] hover:bg-black text-[#FFCC00] font-black uppercase tracking-wider h-11" data-testid="ki-bk-submit">
                    {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> KI denkt nach…</> : <><Sparkles className="w-4 h-4 mr-2" /> Buchungssatz vorschlagen</>}
                </Button>

                {/* Jun-2026 (User-Wunsch b): Konten manuell aus dem SKR51-Kontenplan wählen */}
                <div className="mt-5 pt-5 border-t border-dashed border-gray-200" data-testid="ki-bk-manual">
                    <div className="flex items-center gap-1.5 mb-3 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                        <ListTree className="w-4 h-4 text-[#111827]" /> Konten manuell wählen (SKR51)
                    </div>
                    <div className="space-y-3">
                        <Skr51AccountPicker value={sollKonto} onChange={setSollKonto} label="Soll-Konto" accent="emerald" testid="ki-bk-soll-picker" />
                        <Skr51AccountPicker value={habenKonto} onChange={setHabenKonto} label="Haben-Konto" accent="sky" testid="ki-bk-haben-picker" />
                    </div>
                    <Button onClick={buildManual} variant="outline" className="w-full rounded-sm mt-3 font-bold h-10" data-testid="ki-bk-manual-apply">
                        Buchungssatz aus SKR51 übernehmen
                    </Button>
                    <p className="text-[10px] text-gray-400 mt-2">Betrag &amp; USt-Satz oben werden für die Beträge übernommen.</p>
                </div>

                {/* Jun-2026 (User-Wunsch): SKR51-Kontenplan aufklappbar — nur sichtbar bei Kontenrahmen SKR51 */}
                {form.kontenrahmen === "SKR51" && (
                    <div className="mt-5 pt-5 border-t border-dashed border-gray-200" data-testid="ki-bk-kontenplan">
                        <button onClick={() => setShowKontenplan((s) => !s)}
                            className="w-full flex items-center justify-between text-[11px] font-bold uppercase tracking-wider text-gray-500"
                            data-testid="ki-bk-kontenplan-toggle">
                            <span className="flex items-center gap-1.5"><BookOpen className="w-4 h-4 text-[#111827]" /> SKR51-Kontenplan</span>
                            {showKontenplan ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </button>
                        {showKontenplan && (
                            <div className="mt-3">
                                <SKR51KontenplanTab />
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Ergebnis + Verlauf */}
            <div className="lg:col-span-3 space-y-4">
                {result ? (
                    <KiBuchungssatzResult result={result} onAddToStapel={addToStapel} />
                ) : (
                    <div className="bg-white border border-dashed border-gray-300 rounded-sm p-8 text-center text-gray-400" data-testid="ki-bk-empty">
                        <Wand2 className="w-8 h-8 mx-auto mb-2" />
                        Beschreibe links einen Geschäftsvorfall und lass dir den Buchungssatz vorschlagen.
                    </div>
                )}

                {history.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="ki-bk-history">
                        <div className="flex items-center gap-1.5 mb-2 text-[10px] font-bold uppercase tracking-wider text-gray-400"><History className="w-3.5 h-3.5" /> Letzte Vorschläge</div>
                        <ul className="divide-y divide-gray-100">
                            {history.slice(0, 8).map((h) => (
                                <li key={h.id} className="py-2 flex items-center gap-2 text-sm">
                                    <span className="text-[9px] font-bold uppercase bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-sm">{h.kontenrahmen}</span>
                                    <span className="flex-1 truncate text-gray-700">{h.buchungstext}</span>
                                    <span className="font-mono-num text-xs text-[#111827]">{h.soll_konto}→{h.haben_konto}</span>
                                    <span className="font-mono-num text-xs text-gray-500">{eur(h.betrag_brutto)}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Jun-2026 (User-Wunsch): gesammelte Buchungssätze + DATEV-EXTF-Export */}
                <BuchungsstapelPanel reloadSignal={stapelSignal} />
            </div>
        </div>
    );
}
